import { defineStore } from 'pinia';
import { ref } from 'vue';
import { WardMenuModuleService } from '../services/menuModuleService';
import { GlobalMockupService } from '@/common/services/mockup';
import { MockWardModulesManagementService } from '../services/mockWardModulesManagementService';
import type { MenuModulePayloadModel, MenuModuleModel } from '../types/menuTypes';

export const useMenuModuleStore = defineStore('wardMenu', () => {
  // State
  const modules = ref<MenuModuleModel[]>([]);
  const modulesOrderArray = ref<number[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);

  // Helper to check if mockup is enabled
  const isMockupEnabled = () => GlobalMockupService.isFeatureMockupEnabled('wardMenu');

  // State management functions
  function setModules(newModules: MenuModuleModel[]) {
    modules.value = newModules;
    // Update order array to match
    modulesOrderArray.value = newModules.sort((a, b) => a.order - b.order).map((m) => m.id);
  }

  function setModulesOrderArray(newOrderArray: number[]) {
    modulesOrderArray.value = newOrderArray;
  }

  function setError(errorMessage: string | null) {
    error.value = errorMessage;
  }

  function setLoading(isLoading: boolean) {
    loading.value = isLoading;
  }

  function clearError() {
    error.value = null;
  }

  // CRUD Operations - appelant directement les services
  async function fetchModules() {
    loading.value = true;
    clearError();
    try {
      let fetchedModules: MenuModuleModel[];
      if (isMockupEnabled()) {
        fetchedModules = await MockWardModulesManagementService.getModules();
      } else {
        fetchedModules = await WardMenuModuleService.getModules();
      }
      setModules(fetchedModules);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch modules');
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function createModule(
    newModule: MenuModulePayloadModel,
    _position: string = 'after',
    _oldId?: number
  ) {
    loading.value = true;
    clearError();
    try {
      let createdModule: MenuModuleModel;
      if (isMockupEnabled()) {
        createdModule = await MockWardModulesManagementService.createModule(newModule);
      } else {
        createdModule = await WardMenuModuleService.createModule(newModule);
      }

      // Simple append to end
      const updatedModules = [...modules.value, createdModule];
      setModules(updatedModules);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create module');
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function updateModule(
    updatedData: MenuModuleModel,
    _position: string = 'after',
    _oldId?: number
  ) {
    loading.value = true;
    clearError();
    try {
      let updatedModule: MenuModuleModel;
      if (isMockupEnabled()) {
        updatedModule = await MockWardModulesManagementService.updateModule(updatedData);
      } else {
        updatedModule = await WardMenuModuleService.updateModule(updatedData);
      }

      // Update in array
      const index = modules.value.findIndex((m) => m.id === updatedModule.id);
      if (index !== -1) {
        modules.value[index] = updatedModule;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update module');
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function deleteModule(id: number) {
    loading.value = true;
    clearError();
    try {
      if (isMockupEnabled()) {
        await MockWardModulesManagementService.deleteModule(id);
      } else {
        await WardMenuModuleService.deleteModule(id);
      }

      // Remove from array
      const updatedModules = modules.value.filter((m) => m.id !== id);
      setModules(updatedModules);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete module');
      throw err;
    } finally {
      loading.value = false;
    }
  }

  // Update multiple modules' order in the backend
  async function updateModulesOrder(
    orderedModules: MenuModuleModel[],
    originalModules: MenuModuleModel[]
  ): Promise<void> {
    loading.value = true;
    clearError();
    try {
      const updatePromises = orderedModules.map(async (module, index) => {
        const originalModule = originalModules.find((m) => m.id === module.id);
        if (originalModule && originalModule.order !== index) {
          const updatedModule = { ...module, order: index };

          if (isMockupEnabled()) {
            await MockWardModulesManagementService.updateModule(updatedModule);
          } else {
            await WardMenuModuleService.updateModule(updatedModule);
          }
        }
      });

      await Promise.all(updatePromises);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update modules order');
      throw err;
    } finally {
      loading.value = false;
    }
  }

  return {
    // State
    modules,
    modulesOrderArray,
    loading,
    error,

    // Operations
    fetchModules,
    createModule,
    updateModule,
    deleteModule,
    updateModulesOrder,

    // State management
    setModules,
    setModulesOrderArray,
    setError,
    setLoading,
    clearError,
  };
});
